// adminportal/src/pages/UpdateCompany.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Save, ArrowLeft, AlertTriangle, Server } from 'lucide-react';
import { apiUrl } from '../config';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  isPrimary?: boolean;
  primaryContact?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  allowedIpAddresses?: string;
  serviceAccount?: {
    userId: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    createdAt: string;
  };
}

export default function UpdateCompany() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    companyUrl: '',
    companyState: '',
    primaryContact: '',
    primaryPhone: '',
    primaryEmail: '',
    allowedIpAddresses: '',
    allowAllIps: false,
    createServiceAccount: false,
    serviceAccountUsername: '',
    serviceAccountPassword: '',
    serviceAccountPasswordOption: 'generated' as 'generated' | 'custom' | 'ssh-key',
    sendWelcomeEmail: true,
  });

  // Load company data on component mount
  useEffect(() => {
    if (companyId) {
      loadCompany();
    }
  }, [companyId]);

  const loadCompany = async () => {
    setLoading(true);
    try {
      // Loading company with ID
      // API URL constructed

      // Try the individual company endpoint first
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      const res = await fetch(`${apiUrl('companies')}/${encodeURIComponent(companyId)}`);
      // Response status received
      // Response headers received

      if (!res.ok) {
        await res.text();
        // Individual company endpoint failed
        // Falling back to companies list

        // Fallback: get all companies and filter
        const fallbackRes = await fetch(`${apiUrl('companies')}`);
        if (!fallbackRes.ok) {
          throw new Error(await fallbackRes.text());
        }

        const companies = await fallbackRes.json();
        // Companies loaded from fallback
        // Looking for company ID

        // Find the specific company by ID
        const companyData = companies.companies?.find(
          (c: { companyId: string }) => c.companyId === companyId
        );
        // Found company data

        if (!companyData) {
          // Company not found in list
          throw new Error('Company not found');
        }

        setCompany(companyData);
        setFormData({
          companyName: companyData.companyName || '',
          companyUrl: companyData.companyUrl || '',
          companyState: companyData.companyState || '',
          primaryContact: companyData.primaryContact || '',
          primaryPhone: companyData.primaryPhone || '',
          primaryEmail: companyData.primaryEmail || '',
          allowedIpAddresses: companyData.allowedIpAddresses || '',
          allowAllIps: false,
          createServiceAccount: !!companyData.serviceAccount,
          serviceAccountUsername:
            companyData.serviceAccount?.username ||
            `svc-${companyData.companyName?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || ''}${Math.floor(
              Math.random() * 1000
            )
              .toString()
              .padStart(3, '0')}`,
          serviceAccountPassword: '',
          serviceAccountPasswordOption: 'generated',
          sendWelcomeEmail: true,
        });

        setMessage(`✅ Company "${companyData.companyName}" loaded successfully (fallback method)`);
        return;
      }

      // Individual company endpoint worked
      const companyData = await res.json();
      // Company data loaded from individual endpoint

      setCompany(companyData);
      setFormData({
        companyName: companyData.companyName || '',
        companyUrl: companyData.companyUrl || '',
        companyState: companyData.companyState || '',
        primaryContact: companyData.primaryContact || '',
        primaryPhone: companyData.primaryPhone || '',
        primaryEmail: companyData.primaryEmail || '',
        allowedIpAddresses: companyData.allowedIpAddresses || '',
        allowAllIps: false,
        createServiceAccount: !!companyData.serviceAccount,
        serviceAccountUsername:
          companyData.serviceAccount?.username ||
          `svc-${companyData.companyName?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || ''}${Math.floor(
            Math.random() * 1000
          )
            .toString()
            .padStart(3, '0')}`,
        serviceAccountPassword: '',
        serviceAccountPasswordOption: 'generated',
        sendWelcomeEmail: true,
      });

      setMessage(`✅ Company "${companyId}" loaded successfully`);
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to load company: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Auto-update service account username when company name changes
      if (name === 'companyName' && !company?.serviceAccount) {
        const cleanCompanyName = value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const randomDigits = Math.floor(Math.random() * 1000)
          .toString()
          .padStart(3, '0');
        newData.serviceAccountUsername = `svc-${cleanCompanyName}${randomDigits}`;
      }

      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    setSaving(true);
    setMessage('');

    try {
      // Update company data
      const updateData = {
        companyName: formData.companyName,
        companyUrl: formData.companyUrl,
        companyState: formData.companyState,
        primaryContact: formData.primaryContact,
        primaryPhone: formData.primaryPhone,
        primaryEmail: formData.primaryEmail,
        allowedIpAddresses: formData.allowedIpAddresses || undefined,
        allowAllIps: formData.allowAllIps || undefined,
      };

      const res = await fetch(`${apiUrl('companies')}/${encodeURIComponent(company.companyId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      if (!res.ok) throw new Error(await res.text());

      let serviceAccountResult = null;

      // Create service account if requested
      if (formData.createServiceAccount && formData.primaryEmail) {
        try {
          // Generate company-specific service account email
          const companyDomain = formData.primaryEmail.split('@')[1];
          const serviceAccountEmail = `sftp@${companyDomain}`;

          const serviceAccountData = {
            companyName: formData.companyName,
            companyEmail: serviceAccountEmail,
            companyId: company.companyId,
            customPassword:
              formData.serviceAccountPasswordOption === 'custom'
                ? formData.serviceAccountPassword
                : undefined,
            sendWelcomeEmail: formData.sendWelcomeEmail,
          };

          const serviceRes = await fetch(apiUrl('create-user'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(serviceAccountData),
          });

          if (serviceRes.ok) {
            serviceAccountResult = await serviceRes.json();
            // Service account created
          } else {
            // Service account creation failed
          }
        } catch {
          // Service account creation failed (non-critical)
        }
      }

      let successMessage = `✅ Company "${formData.companyName}" updated successfully`;
      if (serviceAccountResult) {
        successMessage += ` with service account "${serviceAccountResult.serviceAccount?.username}"`;
      }

      setMessage(successMessage);
      setTimeout(() => {
        navigate('/companies');
      }, 1500);
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to update company: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='p-4 max-w-4xl mx-auto'>
        <div className='text-center py-8 text-slate-400'>Loading company...</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className='p-4 max-w-4xl mx-auto'>
        <div className='text-center py-8 text-red-400'>Company not found</div>
      </div>
    );
  }

  return (
    <div className='p-4 max-w-4xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <h1 className='text-2xl font-semibold text-slate-100 flex items-center gap-2'>
          <Building2 className='w-6 h-6' />
          Update Company
        </h1>
        <button
          onClick={() => navigate('/companies')}
          className='text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1'
        >
          <ArrowLeft className='w-4 h-4' />
          Back to Companies
        </button>
      </div>

      {company.isPrimary && (
        <div className='mb-6 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg text-amber-300 flex items-center gap-2'>
          <AlertTriangle className='w-5 h-5' />
          <span>This is the primary company. Some fields may be restricted from modification.</span>
        </div>
      )}

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg border ${
            message.includes('❌')
              ? 'bg-red-900/20 border-red-700/50 text-red-300'
              : 'bg-green-900/20 border-green-700/50 text-green-300'
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Company Name *</label>
            <input
              type='text'
              name='companyName'
              value={formData.companyName}
              onChange={handleInputChange}
              required
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='Enter company name'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Company State *</label>
            <input
              type='text'
              name='companyState'
              value={formData.companyState}
              onChange={handleInputChange}
              required
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='Enter company state'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Company URL</label>
            <input
              type='text'
              name='companyUrl'
              value={formData.companyUrl}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='company.com or https://company.com'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Primary Contact</label>
            <input
              type='text'
              name='primaryContact'
              value={formData.primaryContact}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='Enter primary contact name'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Primary Phone</label>
            <input
              type='tel'
              name='primaryPhone'
              value={formData.primaryPhone}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='Enter primary phone number'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-300 mb-2'>Primary Email</label>
            <input
              type='email'
              name='primaryEmail'
              value={formData.primaryEmail}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='Enter primary email address'
            />
          </div>

          <div className='md:col-span-2'>
            <label className='block text-sm font-medium text-slate-300 mb-2'>
              Allowed IP Addresses (Optional)
            </label>
            <input
              type='text'
              name='allowedIpAddresses'
              value={formData.allowedIpAddresses}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
              placeholder='203.0.113.0/24, 198.51.100.0/24 (comma-separated)'
            />
            <p className='text-xs text-slate-400 mt-1'>
              IP addresses or CIDR blocks allowed to access CDN (port 443) and SFTP server (port 22).
              Comma-separated. Examples: 203.0.113.0/24, 198.51.100.0/24, 192.0.2.0/24
            </p>
            
            {/* Security warning for 0.0.0.0/0 */}
            {(formData.allowedIpAddresses?.includes('0.0.0.0/0') || formData.allowedIpAddresses === '0.0.0.0/0') && (
              <div className='mt-3 p-3 bg-red-900/20 border border-red-700/50 rounded-lg'>
                <label className='flex items-start gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    name='allowAllIps'
                    checked={formData.allowAllIps || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, allowAllIps: e.target.checked }))}
                    className='mt-1'
                  />
                  <div className='flex-1'>
                    <span className='text-sm font-medium text-red-300 block mb-1'>
                      ⚠️ Security Warning: Allow All IPs (0.0.0.0/0)
                    </span>
                    <p className='text-xs text-red-200'>
                      You are allowing access from all IP addresses. This disables IP whitelisting and allows anyone on the internet to access the CDN and SFTP server. 
                      This action will be logged for security auditing. Only check this box if you understand the security implications.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Service Account Section */}
        <div className='border-t border-white/10 pt-6'>
          <h3 className='text-lg font-medium text-slate-200 mb-4'>Service Account</h3>

          {/* Display existing service account information */}
          {company.serviceAccount && (
            <div className='bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-4'>
              <h4 className='text-sm font-medium text-blue-400 mb-3 flex items-center gap-2'>
                <Server className='w-4 h-4' />
                Existing Service Account
              </h4>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
                <div>
                  <span className='text-slate-400'>Username:</span>
                  <span className='text-slate-200 ml-2 font-mono'>
                    {company.serviceAccount.username}
                  </span>
                </div>
                <div>
                  <span className='text-slate-400'>Email:</span>
                  <span className='text-slate-200 ml-2'>{company.serviceAccount.email}</span>
                </div>
                <div>
                  <span className='text-slate-400'>Role:</span>
                  <span className='text-slate-200 ml-2'>{company.serviceAccount.role}</span>
                </div>
                <div>
                  <span className='text-slate-400'>Created:</span>
                  <span className='text-slate-200 ml-2'>
                    {new Date(company.serviceAccount.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className='space-y-4'>
            <label className='flex items-center'>
              <input
                type='checkbox'
                checked={formData.createServiceAccount}
                onChange={e =>
                  setFormData(prev => ({ ...prev, createServiceAccount: e.target.checked }))
                }
                className='mr-2'
              />
              <span className='text-slate-300'>
                Create service account for automated operations
              </span>
            </label>

            {formData.createServiceAccount && (
              <div className='space-y-4 pl-6 border-l-2 border-blue-500/30'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>
                    Service Account Username
                  </label>
                  <input
                    type='text'
                    name='serviceAccountUsername'
                    value={formData.serviceAccountUsername}
                    readOnly
                    className='w-full px-3 py-2 border border-white/10 rounded-lg form-input bg-slate-600/30 text-slate-200 cursor-not-allowed'
                    placeholder='Enter service account username'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>
                    Password Option
                  </label>
                  <div className='flex gap-4'>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        name='serviceAccountPasswordOption'
                        value='generated'
                        checked={formData.serviceAccountPasswordOption === 'generated'}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            serviceAccountPasswordOption: e.target.value as 'generated' | 'custom',
                          }))
                        }
                        className='mr-2'
                      />
                      <span className='text-slate-300'>Generate random password</span>
                    </label>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        name='serviceAccountPasswordOption'
                        value='custom'
                        checked={formData.serviceAccountPasswordOption === 'custom'}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            serviceAccountPasswordOption: e.target.value as 'generated' | 'custom',
                          }))
                        }
                        className='mr-2'
                      />
                      <span className='text-slate-300'>Custom password</span>
                    </label>
                  </div>
                </div>

                {formData.serviceAccountPasswordOption === 'custom' && (
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>
                      Custom Password
                    </label>
                    <input
                      type='password'
                      name='serviceAccountPassword'
                      value={formData.serviceAccountPassword}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-white/10 rounded-lg form-input'
                      placeholder='Enter custom password'
                    />
                  </div>
                )}

                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={formData.sendWelcomeEmail}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, sendWelcomeEmail: e.target.checked }))
                    }
                    className='mr-2'
                  />
                  <span className='text-slate-300'>Send welcome email with credentials</span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className='flex gap-4 pt-4'>
          <button
            type='button'
            onClick={() => navigate('/companies')}
            className='px-6 py-2 border border-white/10 rounded-lg text-slate-200 hover:bg-white/10 transition-colors'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={saving}
            className='px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2'
          >
            <Save className='w-4 h-4' />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
